import React, { useState, useEffect } from "react";
import { GoogleLogo, FacebookLogo, Play, Pause, FloppyDisk } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { formatCurrency, formatRoas, formatPercent, formatNumber } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_VARIANT = { live: "success", paused: "warning", closed: "secondary" };
const PLATFORM_ICON = { google: GoogleLogo, meta: FacebookLogo };

export default function CampaignDetailSheet({ campaign, open, onOpenChange, onUpdated }) {
  const [goals, setGoals] = useState({ max_cpl: "", pause_on_cpl_exceed: true, min_roas_for_scale: "", increase_spend_on_high_roas: true, scale_percent: 20 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign?.goals) {
      setGoals({
        max_cpl: campaign.goals.max_cpl ?? "",
        pause_on_cpl_exceed: campaign.goals.pause_on_cpl_exceed ?? true,
        min_roas_for_scale: campaign.goals.min_roas_for_scale ?? "",
        increase_spend_on_high_roas: campaign.goals.increase_spend_on_high_roas ?? true,
        scale_percent: campaign.goals.scale_percent ?? 20,
      });
    } else if (campaign) {
      setGoals({ max_cpl: "", pause_on_cpl_exceed: true, min_roas_for_scale: "", increase_spend_on_high_roas: true, scale_percent: 20 });
    }
  }, [campaign]);

  if (!campaign) return null;
  const PlatformIcon = PLATFORM_ICON[campaign.platform] || GoogleLogo;

  const toggleStatus = async () => {
    const newStatus = campaign.status === "live" ? "paused" : "live";
    try {
      const res = await api.patch(`/campaigns/${campaign.id}`, { status: newStatus });
      onUpdated(res.data);
      toast.success(`Campaign ${newStatus === "live" ? "resumed" : "paused"}`);
    } catch (e) {
      toast.error("Failed to update campaign status");
    }
  };

  const saveGoals = async () => {
    setSaving(true);
    try {
      const payload = {
        goals: {
          max_cpl: goals.max_cpl === "" ? null : parseFloat(goals.max_cpl),
          pause_on_cpl_exceed: goals.pause_on_cpl_exceed,
          min_roas_for_scale: goals.min_roas_for_scale === "" ? null : parseFloat(goals.min_roas_for_scale),
          increase_spend_on_high_roas: goals.increase_spend_on_high_roas,
          scale_percent: parseFloat(goals.scale_percent) || 20,
        },
      };
      const res = await api.patch(`/campaigns/${campaign.id}`, payload);
      onUpdated(res.data);
      toast.success("Campaign goals saved");
    } catch (e) {
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto" data-testid="campaign-detail-sheet">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center">
              <PlatformIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Badge variant={STATUS_VARIANT[campaign.status]} className="capitalize">{campaign.status}</Badge>
          </div>
          <SheetTitle data-testid="campaign-detail-title">{campaign.name}</SheetTitle>
          <SheetDescription className="capitalize">{campaign.platform} Ads campaign</SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-4 my-6">
          <Metric label="Spend" value={formatCurrency(campaign.spend)} />
          <Metric label="Budget" value={formatCurrency(campaign.budget)} />
          <Metric label="Impressions" value={formatNumber(campaign.impressions)} />
          <Metric label="Clicks" value={formatNumber(campaign.clicks)} />
          <Metric label="CTR" value={formatPercent(campaign.ctr)} />
          <Metric label="CPC" value={formatCurrency(campaign.cpc)} />
          <Metric label="CPL" value={formatCurrency(campaign.cpl)} />
          <Metric label="ROAS" value={formatRoas(campaign.roas)} />
          <Metric label="Conversions" value={formatNumber(campaign.conversions)} />
        </div>

        <Button
          variant={campaign.status === "live" ? "outline" : "default"}
          className="w-full mb-6"
          onClick={toggleStatus}
          disabled={campaign.status === "closed"}
          data-testid="campaign-toggle-status-button"
        >
          {campaign.status === "live" ? <Pause className="h-4 w-4 mr-2" weight="bold" /> : <Play className="h-4 w-4 mr-2" weight="bold" />}
          {campaign.status === "live" ? "Pause Campaign" : "Resume Campaign"}
        </Button>

        <Separator className="mb-6" />

        <div className="space-y-5">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Autonomous Rules</p>

          <div className="space-y-2">
            <Label htmlFor="max-cpl">Pause when CPL exceeds ($)</Label>
            <div className="flex items-center gap-3">
              <Input id="max-cpl" type="number" step="0.01" placeholder="e.g. 25.00" value={goals.max_cpl}
                onChange={(e) => setGoals({ ...goals, max_cpl: e.target.value })} data-testid="input-max-cpl" />
              <Switch checked={goals.pause_on_cpl_exceed} onCheckedChange={(v) => setGoals({ ...goals, pause_on_cpl_exceed: v })} data-testid="switch-pause-on-cpl" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="min-roas">Scale budget when ROAS exceeds (x)</Label>
            <div className="flex items-center gap-3">
              <Input id="min-roas" type="number" step="0.1" placeholder="e.g. 3.0" value={goals.min_roas_for_scale}
                onChange={(e) => setGoals({ ...goals, min_roas_for_scale: e.target.value })} data-testid="input-min-roas" />
              <Switch checked={goals.increase_spend_on_high_roas} onCheckedChange={(v) => setGoals({ ...goals, increase_spend_on_high_roas: v })} data-testid="switch-scale-on-roas" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scale-pct">Budget increase (%)</Label>
            <Input id="scale-pct" type="number" step="1" value={goals.scale_percent}
              onChange={(e) => setGoals({ ...goals, scale_percent: e.target.value })} data-testid="input-scale-percent" />
          </div>

          <Button className="w-full" onClick={saveGoals} disabled={saving} data-testid="save-goals-button">
            <FloppyDisk className="h-4 w-4 mr-2" weight="bold" />
            {saving ? "Saving..." : "Save Rules"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}
